import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoadingState from "./LoadingState";

/**
 * Redirects to /login if there's no Supabase session. While the initial
 * session check is in flight, renders a loading state instead of
 * flashing the login page.
 *
 * Also the choke point for the forced password change (migration 62). Every
 * authenticated route in App.tsx is wrapped in this, so gating here covers all
 * of them at once — and, unlike gating in LoginPage, it survives a reload, a
 * bookmarked deep link, and a session restored from storage.
 */
export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();
  const location = useLocation();

  if (loading && !session) {
    return <LoadingState label="Signing in…" />;
  }

  if (!session) {
    // Keep the query string too — e.g. /buy?quantity=12 from the registry CTA.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Wait for the profile before deciding — a null profile mid-fetch would let
  // a flagged user straight through on every reload.
  if (loading || !profile) {
    return <LoadingState label="Signing in…" />;
  }

  if (profile.must_change_password) {
    return <Navigate to="/reset-password" replace />;
  }

  return <>{children}</>;
}
